import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';

export interface SecurityRole {
	id: number;
	name: string;
	createdAt: string;
	createdBy: string;
	deleted: boolean;
	description?: string;
	updatedAt: string;
	updatedBy?: string;
	workplace?: string;
	permissions: any[];
}

export interface GetSecurityRolesParams {
	page?: number;
	itemsPerPage?: number;
}

export interface SecurityRolesResponse {
	items: SecurityRoleResponse[];
	total: number;
}

export interface SecurityRolePermission {
	delete: boolean;
	view: boolean;
	edit: boolean;
	execute: boolean;
	id: number;
	permission: {
		id: number;
		label: string;
		permissionGroup: string;
	};
}

export interface SecurityRoleType {
	'@id': string;
	'@type': string;
	id: number;
	label: string;
}

export interface SecurityRoleResponse {
	'@id': string;
	id: number;
	createdAt: string;
	createdBy: string;
	deleted: boolean;
	identifier: string;
	name: string;
	securityRoleType: SecurityRoleType;
	securityRolePermission: SecurityRolePermission[];
	updatedAt: string;
	updatedBy?: string;
	workplace?: string;
	description?: string;
}

export interface CreateSecurityRolePermission {
	permission: string;
	view?: boolean;
	edit?: boolean;
	delete?: boolean;
	execute?: boolean;
}

export interface SecurityRoleType {
	'@id': string;
	'@type': string;
	id: number;
	label: string;
	deleted: boolean;
}

export interface CreateSecurityRoleRequest {
	name: string;
	description: string;
	workplace?: string;
	securityRoleType?: string;
	securityRolePermission: CreateSecurityRolePermission[];
}

/**
 * Get security roles with pagination
 */
export const getSecurityRoles = async (
	params: GetSecurityRolesParams = {}
): Promise<SecurityRoleResponse[]> => {
	try {
		const { itemsPerPage = 50 } = params;
		const response = await paginationService.getWithHandler(
			'/security_roles',
			undefined,
			itemsPerPage,
		);
		const rolesData = response?.member || response?.['hydra:member'] || [];
		return rolesData.map((sr) => ({ ...sr, id: sr['@id'].split('/').pop() }));
	} catch (error: any) {
		console.error('Failed to fetch security roles:', error);
		return [];
	}
};

/**
 * Create a new security role
 */
export const createSecurityRole = async (
	roleData: CreateSecurityRoleRequest
): Promise<SecurityRole> => {
	try {
		const response = await apiService.post('/security_roles', roleData);
		return apiService.responseHandler(response);
	} catch (error: any) {
		console.error('Failed to create security role:', error);
		throw error;
	}
};

/**
 * Update an existing security role
 */
export const updateSecurityRole = async (
	roleId: number,
	roleData: CreateSecurityRoleRequest
): Promise<SecurityRole> => {
	try {
		const response = await apiService.put(`/security_roles/${roleId}`, roleData);
		return apiService.responseHandler(response);
	} catch (error: any) {
		console.error('Failed to update security role:', error);
		throw error;
	}
};

/**
 * Get security role types
 */
export const getSecurityRoleTypes = async (): Promise<SecurityRoleType[]> => {
	try {
		const data = await paginationService.getWithHandler('/security_role_types');
		return data?.member || data?.['hydra:member'] || [];
	} catch (error: any) {
		console.error('Failed to fetch security role types:', error);
		return [];
	}
};

/**
 * Fetch permission groups
 */
export const fetchPermissionGroups = async (): Promise<Map<string, string>> => {
	try {
		const data = await paginationService.getWithHandler('/permission_groups');
		const groupsMap = new Map<string, string>();
		const members = data?.member || data?.['hydra:member'] || [];
		members.forEach((group: { '@id': string; label: string }) => {
			groupsMap.set(group['@id'], group.label);
		});
		return groupsMap;
	} catch (error: any) {
		console.error('Failed to fetch permission groups:', error);
		return new Map();
	}
};

export const securityRolesService = {
	getSecurityRoles,
	createSecurityRole,
	updateSecurityRole,
	fetchPermissionGroups,
	getSecurityRoleTypes,
};

export default securityRolesService;
